import styled from "@emotion/styled";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import assertNever from "assert-never";
import { Instant } from "@js-joda/core";
import * as React from "react";
import * as Ons from "react-onsenui";
import { API } from "../API";
import { isLoading, Loading, loading } from "../Loading";
import * as messages from "../messages";
import { ChatRoomId, VisitInstanceDetailsWithType } from "../messages";
import { STRINGS } from "../strings";
import { AppAgenciesState } from "../system/AppState/AppAgenciesState";
import { Visit } from "../system/AppState/AppVisitsState";
import { ChatRoom, SendingChatMessage } from "../system/AppState/ChatState";
import { translate } from "../translate";
import { GoToVisitParams } from "../types/goToVisitTypes";
import { KeyedList, List } from "../utils/List";
import { memoizeAll2 } from "../utils/memoizeAll";
import { undefinedToNull } from "../utils/undefinedToNull";
import ChatMessageView from "./ChatMessageView";
import ChatRoomInputPanel from "./ChatRoomInputPanel";
import { AppButton } from "./Generic/AppButton";
import { ScreenSkeleton } from "./ScreenSkeleton";
import { simpleAlert } from "./SimpleAlert";

export interface ChatRoomScreenProps {
  headerTitle: string;
  agencyMembers: AppAgenciesState | Loading;
  chatRoom: ChatRoom | null;
  visitData?: Visit | VisitInstanceDetailsWithType | null;
  sendingMessages: List<SendingChatMessage> | null;
  getSignedPhotoUrl: API["getSignedPhotoUrl"];
  onBackClick: () => void;
  onChatMessageLastViewed: (chatRoomId: ChatRoomId, messageTime: Instant) => void;
  onSendMessage: (chatRoomId: ChatRoomId, message: { value: string; images: string[] }) => void;
  // null = don't display "goToVisit" button
  goToVisit: ((params: GoToVisitParams) => void) | null;
  initialMessage?: string;
}

export interface ChatRoomScreenState {
  // TODO This should be a prop so that it isn't lost when the user leaves the chat screen and later comes back
  inputMessage: string;
  hasActiveAgencyMembers: boolean;
  images?: string[];
}

export class ChatRoomScreen extends React.Component<ChatRoomScreenProps, ChatRoomScreenState> {
  constructor(props: ChatRoomScreenProps) {
    super(props);

    // Indicate that the chat is active by default
    let chatActive = true;

    // Check if there is chat room data
    if (this.props.chatRoom !== null) {
      if (this.props.chatRoom.chatRoomType.type === "CaregiverVisitChat") {
        // Check if there is data for the given visit
        if (this.props.visitData === undefined || this.props.visitData === null) {
          chatActive = false;
        }
      } else if (this.props.chatRoom.chatRoomType.type === "AgencyMemberCaregiverChat") {
        let allAgencyMembers;
        if (!isLoading(this.props.agencyMembers)) {
          allAgencyMembers = this.props.agencyMembers.allAgencyMembers;
        }

        // If there are no agency members, chat is inactive.
        if (allAgencyMembers === undefined) {
          chatActive = false;
        } else {
          // The agency member the caregiver is talking to.
          const agencyMemberToChatWith = allAgencyMembers.get(
            this.props.chatRoom.chatRoomType.agencyMemberId
          );

          if (
            agencyMemberToChatWith === undefined ||
            agencyMemberToChatWith.details.status !== "Active"
          ) {
            chatActive = false;
          } else {
            let agencyMembersInChatRoom = 0;
            let inactiveAgencyMembersCount = 0;

            // Run on each member in the chat room
            for (const member of this.props.chatRoom.members) {
              // Check if the member is an agency member
              if (member.user.type === "AgencyMember") {
                // Increment the chat room agency members count
                agencyMembersInChatRoom++;
                const agencyMemberInChat = allAgencyMembers.get(member.user.agencyMemberId);

                // Check if the agency member is undefined which means that the agency member is inactive
                if (
                  agencyMemberInChat === undefined ||
                  agencyMemberInChat.details.status !== "Active"
                ) {
                  // Increment the chat room inactive agency members count
                  inactiveAgencyMembersCount++;
                }
              }
            }

            // Check if all the agency members are inactive
            if (
              agencyMembersInChatRoom > 0 &&
              inactiveAgencyMembersCount === agencyMembersInChatRoom
            ) {
              chatActive = false;
            }
          }
        }
      } else if (this.props.chatRoom.chatRoomType.type === "CaregiverPatientChat") {
        chatActive = this.props.visitData !== null;
      }
    }

    // Check if there are no active agency members in the chat
    if (!chatActive) {
      simpleAlert({
        title: translate(STRINGS.CHAT_NOT_ACTIVE_TITLE),
        message: translate(STRINGS.CHAT_NOT_ACTIVE_MESSAGE),
        icon: "warning",
      });
    }

    this.state = {
      inputMessage: this.props.initialMessage !== undefined ? this.props.initialMessage : "",
      hasActiveAgencyMembers: chatActive,
    };
  }

  handleInputChange = (val: string): void => {
    this.setState((_prevState: ChatRoomScreenState) => {
      return {
        inputMessage: val,
      };
    });
  };

  handleSelectImages = (images: string[]): void => {
    this.setState({ images: images });
  };

  handleRemoveImage = (image: string): void => {
    this.setState((prevState: ChatRoomScreenState) => {
      if (prevState.images === undefined) {
        return {};
      }

      const newImages = prevState.images.filter((i) => i !== image);

      return {
        images: newImages.length > 0 ? newImages : undefined,
      };
    });
  };

  handleSend = (): void => {
    if (this.props.chatRoom === null) {
      return;
    }

    const isMessageNotEmpty = this.state.inputMessage.trim().length > 0;
    const isImagesNotEmpty = this.state.images !== undefined && this.state.images.length > 0;

    if (isMessageNotEmpty || isImagesNotEmpty) {
      this.props.onSendMessage(this.props.chatRoom.id, {
        value: this.state.inputMessage,
        images: this.state.images ?? [],
      });

      this.setState(
        (_prevState: ChatRoomScreenState) => {
          return {
            inputMessage: "",
            images: undefined,
          };
        },
        () => {
          if (this.inputRef !== null) {
            this.inputRef.focus();
          }
        }
      );
    }
  };

  scrollToBottom(): void {
    if (this.messagesCont === null) {
      throw new Error("Component is unmounted");
    }

    this.messagesCont.scrollTop = this.messagesCont.scrollHeight;

    // if (this.state.openAvatarPopup !== null) {
    //     this.closeAvatarPopover();
    // }
  }

  handleWindowResize = (): void => {
    if (this.fullWindowHeight === null || this.messagesCont === null) {
      return;
    }

    const height = window.innerHeight;

    if (height < this.fullWindowHeight) {
      // Keyboard was just opened
      this.messagesCont.scrollTop += this.fullWindowHeight - height;
    } else {
      // Keyboard was just closed
    }
  };

  fullWindowHeight: number | null = null;

  componentDidMount(): void {
    this.emitLastViewed(null);

    this.scrollToBottom();

    window.addEventListener("resize", this.handleWindowResize);

    // We are assuming that the keyboard is not currently open:
    this.fullWindowHeight = window.innerHeight;
  }

  componentWillUnmount(): void {
    window.removeEventListener("resize", this.handleWindowResize);
  }

  componentDidUpdate(prevProps: ChatRoomScreenProps): void {
    this.emitLastViewed(prevProps);
  }

  messagesCont: HTMLElement | null = null;
  inputRef: HTMLTextAreaElement | null = null;

  render(): JSX.Element {
    const chatRoom = this.props.chatRoom;

    return (
      <ScreenSkeleton
        title={this.props.headerTitle}
        titleBarStyle={ScreenSkeleton.TitleBarStyle.PRIMARY}
        onBackClick={this.props.onBackClick}
        className="ChatRoomScreen__skeleton"
      >
        {this.renderGoToVisitButton(chatRoom)}
        <div ref={(e) => (this.messagesCont = e)} className="ChatRoomScreen__messages">
          {this.renderGoToVisitButtonPlaceholder(chatRoom)}
          <ChatMessagesView
            agencyMembers={this.props.agencyMembers}
            chatRoom={chatRoom !== null ? chatRoom : loading()}
            sendingMessages={this.props.sendingMessages}
            getSignedPhotoUrl={this.props.getSignedPhotoUrl}
          />
        </div>
        <ChatRoomInputPanel
          inputRef={(e) => (this.inputRef = e)}
          images={this.state.images ?? []}
          value={this.state.inputMessage}
          disabled={!this.state.hasActiveAgencyMembers}
          onChangeValue={this.handleInputChange}
          onClickRemoveImages={this.handleRemoveImage}
          onSelectImages={this.handleSelectImages}
          onSubmit={this.handleSend}
        />
      </ScreenSkeleton>
    );
  }

  emitLastViewed(prevProps: ChatRoomScreenProps | null): void {
    if (this.props.chatRoom === null) {
      return;
    }

    let scrollToBottom = false;

    // TODO don't scroll to bottom when (only) a sendingMessage was replaced with a real message

    const lastMessage = this.props.chatRoom.messages.last();
    if (lastMessage !== undefined) {
      if (prevProps === null || prevProps.chatRoom === null) {
        this.props.onChatMessageLastViewed(this.props.chatRoom.id, lastMessage.createdAt);
        scrollToBottom = true;
      } else {
        const prevLastMessage = prevProps.chatRoom.messages.last();
        if (prevLastMessage === undefined || lastMessage.createdAt !== prevLastMessage.createdAt) {
          this.props.onChatMessageLastViewed(this.props.chatRoom.id, lastMessage.createdAt);
          scrollToBottom = true;
        }
      }
    }

    if (prevProps === null || this.props.sendingMessages !== prevProps.sendingMessages) {
      scrollToBottom = true;
    }

    if (scrollToBottom) {
      this.scrollToBottom();
    }
  }

  private renderGoToVisitButtonPlaceholder(chatRoom: ChatRoom | null): JSX.Element | null {
    if (
      chatRoom === null ||
      chatRoom.chatRoomType.type !== "CaregiverVisitChat" ||
      this.props.goToVisit === null
    ) {
      return null;
    }
    return <div className="ChatRoomScreen__goToVisitButtonPlaceholder" />;
  }
  private renderGoToVisitButton(chatRoom: ChatRoom | null): JSX.Element | null {
    const goToVisit = this.props.goToVisit;
    const visitData = undefinedToNull(this.props.visitData);

    if (
      chatRoom === null ||
      chatRoom.chatRoomType.type !== "CaregiverPatientChat" ||
      visitData === null ||
      goToVisit === null
    ) {
      return null;
    }

    let goToVisitParams: GoToVisitParams;

    switch (visitData.type) {
      case "Available":
        goToVisitParams = {
          type: visitData.type,
          visitId: visitData.publicVisitBroadcastDetails.id,
        };
        break;
      case "Onboarding":
        goToVisitParams = {
          type: visitData.type,
          visitId: visitData.publicVisitBroadcastDetails.id,
        };
        break;
      case "VisitInstance":
        goToVisitParams = {
          type: visitData.type,
          visitInstanceId: visitData.id,
        };
        break;
      default:
        assertNever(visitData);
    }

    return (
      <AppButton
        className="ChatRoomScreen__goToVisitButton"
        onClick={() => goToVisit(goToVisitParams)}
      >
        {translate(STRINGS.GO_TO_VISIT)}
      </AppButton>
    );
  }
}

interface ChatMessagesViewProps {
  chatRoom: ChatRoom | Loading;
  sendingMessages: List<SendingChatMessage> | null;
  agencyMembers: AppAgenciesState | Loading;
  getSignedPhotoUrl: API["getSignedPhotoUrl"];
}

interface ChatMessagesViewState {
  // Value is requestId
  openAvatarPopup: string | null;
}

class ChatMessagesView extends React.PureComponent<ChatMessagesViewProps, ChatMessagesViewState> {
  constructor(props: ChatMessagesViewProps) {
    super(props);

    this.state = {
      openAvatarPopup: null,
    };
  }

  render() {
    if (isLoading(this.props.chatRoom)) {
      return (
        <div className="ChatRoomScreen__loadingChat">
          <Ons.ProgressCircular indeterminate={true} />
        </div>
      );
    }

    return this.renderMessages(this.props.chatRoom.messages);
  }

  avatarRefs = new Map<string, HTMLElement>();

  setAvatarRef(requestId: string, e: HTMLElement | null) {
    if (e === null) {
      this.avatarRefs.delete(requestId);
    } else {
      this.avatarRefs.set(requestId, e);
    }
  }

  handleOpenAvatarPopover = (requestId: string) => {
    this.setState(() => ({
      openAvatarPopup: requestId,
    }));
  };

  handleCloseAvatarPopover = () => {
    this.setState(() => ({
      openAvatarPopup: null,
    }));
  };

  renderMessages(msgs: KeyedList<messages.ChatRoomMessage, "requestId">): JSX.Element {
    // We must put the react components for the server chat messages
    // together in the same array with the react components of the sending
    // chat messages. This is needed so that the react reconciliation
    // algorithm will be able to detect when a sending message changes into
    // a server message (so that the CSS transition animation will work)

    let lastSeen: Instant | null = null;
    if (!isLoading(this.props.chatRoom)) {
      lastSeen = agencyMembersGreatestLastSeen(this.props.chatRoom.members);
    }

    let allMessages = msgs.map((chatMessage) => (
      <ChatMessageView
        key={chatMessage.requestId}
        message={chatMessage}
        sendingMessage={null}
        seenStatus={isSeenByAgencyMember(chatMessage, lastSeen)}
        author={chatMessageAuthor(chatMessage.user, this.props.agencyMembers)}
        avatarPopoverOpen={this.state.openAvatarPopup === chatMessage.requestId}
        getSignedPhotoUrl={this.props.getSignedPhotoUrl}
        onOpenAvatarPopover={this.handleOpenAvatarPopover}
        onCloseAvatarPopover={this.handleCloseAvatarPopover}
      />
    ));

    if (this.props.sendingMessages !== null) {
      for (const sendingMessage of this.props.sendingMessages) {
        allMessages = allMessages.append(
          <ChatMessageView
            key={sendingMessage.requestId}
            message={null}
            seenStatus={false}
            sendingMessage={sendingMessage}
            author={chatMessageAuthorSelf}
            avatarPopoverOpen={this.state.openAvatarPopup === sendingMessage.requestId}
            getSignedPhotoUrl={this.props.getSignedPhotoUrl}
            onOpenAvatarPopover={this.handleOpenAvatarPopover}
            onCloseAvatarPopover={this.handleCloseAvatarPopover}
          />
        );
      }
    }

    return <AutoAnimate>{allMessages}</AutoAnimate>;
  }
}

function AutoAnimate(props: { children: React.ReactNode }) {
  const [animate] = useAutoAnimate<HTMLDivElement>();

  return <AutoAnimate.Root ref={animate}>{props.children}</AutoAnimate.Root>;
}

AutoAnimate.Root = styled.div``;

function agencyMembersGreatestLastSeen(
  members: KeyedList<messages.ChatRoomMember, "user">
): Instant | null {
  let latestLastViewedOfAgency: Instant | null = null;

  for (const member of members) {
    // Check if the chat member is an agency member
    if (member.user.type === "AgencyMember") {
      if (latestLastViewedOfAgency === null) {
        latestLastViewedOfAgency = member.lastViewed;
      } else {
        if (member.lastViewed.isAfter(latestLastViewedOfAgency)) {
          latestLastViewedOfAgency = member.lastViewed;
        }
      }
    }
  }

  return latestLastViewedOfAgency;
}

function isSeenByAgencyMember(
  message: messages.ChatRoomMessage,
  lastSeen: Instant | null
): boolean {
  let result = false;

  if (message.user.type === "Caregiver") {
    if (lastSeen !== null) {
      if (!message.createdAt.isAfter(lastSeen)) {
        result = true;
      }
    }
  }

  return result;
}

const chatMessageAuthor: (
  chatRoomUser: messages.ChatRoomUser,
  appAgenciesState: AppAgenciesState | Loading
) => ChatMessageAuthor = memoizeAll2(chatMessageAuthorImpl);

function chatMessageAuthorImpl(
  chatRoomUser: messages.ChatRoomUser,
  appAgenciesState: AppAgenciesState | Loading
): ChatMessageAuthor {
  switch (chatRoomUser.type) {
    case "Caregiver":
      return chatMessageAuthorSelf;
    case "AgencyMember": {
      if (isLoading(appAgenciesState)) {
        return {
          type: "AgencyMember",
          agencyMember: loading(),
          agency: loading(),
        };
      }

      const agencyMember = appAgenciesState.allAgencyMembers.get(chatRoomUser.agencyMemberId);

      if (agencyMember === undefined) {
        return {
          type: "AgencyMember",
          agencyMember: loading(),
          agency: loading(),
        };
      }
      const agency = appAgenciesState.agencies.get(agencyMember.agencyId);
      if (agency === undefined) {
        return {
          type: "AgencyMember",
          agencyMember: agencyMember.details,
          agency: loading(),
        };
      }

      return {
        type: "AgencyMember",
        agencyMember: agencyMember.details,
        agency: agency.agency,
      };
    }
    default:
      return assertNever(chatRoomUser);
  }
}

const chatMessageAuthorSelf: ChatMessageAuthor = {
  type: "Self",
};

export type ChatMessageAuthor = ChatMessageAuthor.Self | ChatMessageAuthor.AgencyMember;

declare namespace ChatMessageAuthor {
  export interface Self {
    type: "Self";
  }

  export interface AgencyMember {
    type: "AgencyMember";
    agencyMember: messages.AgencyMember | Loading;
    agency: messages.Agency | Loading;
  }
}

export enum ChatMessageSender {
  Received,
  Sending,
  Sent,
}
